import React, { createContext, useEffect, useState } from "react";
import { db, auth } from "../firebase/config";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { isSameDay, startOfDay } from "date-fns";

export const RemindersContext = createContext();

export function RemindersProvider({ children }) {
  const [reminders, setReminders] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  /* ---------------------------------------------------
     AUTH LISTENER (REAL MULTI-USER FIX)
  --------------------------------------------------- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, []);

  /* ---------------------------------------------------
     FETCH REMINDERS PER USER
  --------------------------------------------------- */
  useEffect(() => {
    if (!currentUser?.uid) {
      setReminders([]);
      return;
    }

    const remindersQuery = query(
      collection(db, "reminders"),
      where("userId", "==", currentUser.uid)
    );

    const unsub = onSnapshot(remindersQuery, (snap) => {
      setReminders(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            isCompleted: data.isCompleted || false,
            date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
            completedAt: data.completedAt
              ? data.completedAt.toDate?.()
                ? data.completedAt.toDate()
                : new Date(data.completedAt)
              : null,
          };
        })
      );
    });

    return () => unsub();
  }, [currentUser]);

  /* ---------------------------------------------------
     ACTIONS
  --------------------------------------------------- */
  const addReminder = async (title, date) => {
    if (!currentUser?.uid) return;

    await addDoc(collection(db, "reminders"), {
      title,
      date: new Date(date),
      createdAt: serverTimestamp(),
      isCompleted: false,
      completedAt: null,
      userId: currentUser.uid, // 🔑 CRITICAL
    });
  };

  const deleteReminder = async (id) => {
    await deleteDoc(doc(db, "reminders", id));
  };

  const toggleReminder = async (id) => {
    const reminder = reminders.find((r) => r.id === id);
    if (!reminder) return;

    await updateDoc(doc(db, "reminders", id), {
      isCompleted: !reminder.isCompleted,
      completedAt: reminder.isCompleted ? null : startOfDay(new Date()),
    });
  };

  /* ---------------------------------------------------
     DERIVED DATA
  --------------------------------------------------- */
  const todayReminders = reminders.filter((r) => isSameDay(r.date, new Date()));

  return (
    <RemindersContext.Provider
      value={{
        reminders,
        todayReminders,
        addReminder,
        deleteReminder,
        toggleReminder,
      }}
    >
      {children}
    </RemindersContext.Provider>
  );
}
